import { Text } from "@hackthenorth/north";
import React from "react";
import styled from "styled-components";

import { ChipIcon, GearIcon, NetworkIcon, QRCodeIcon } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { BalanceChip } from "src/views/goose-games/components/balanceChip";
import {
  Quest,
  QuestBackgroundColorMap,
  QuestPrimaryColorMap,
  QuestType,
} from "src/views/goose-games/types";

import { QUESTS } from "../../constants";

interface Props {
  quest: Quest;
  progress?: number;
  preview?: boolean;
}

const ScannedQuestView = ({ quest, progress = 0, preview = false }: Props) => {
  const completedQuest = !preview && progress >= quest.completion_limit;

  return (
    <StyledDiv>
      <FlexHeading>
        <TypeContainer>
          <IconOutline type={quest.type}>
            {quest.type === QuestType.Scanned ? (
              <QRCodeIcon width={24} color={QUESTS.SCANNED.color} />
            ) : quest.type === QuestType.OrganizerApproved ? (
              <GearIcon width={24} />
            ) : quest.type === QuestType.Badge ? (
              <ChipIcon width={24} />
            ) : (
              <NetworkIcon width={24} />
            )}
          </IconOutline>
          <TitleText mods="heading h3">{quest.title}</TitleText>
        </TypeContainer>
        <TypeContainer>
          {!preview && (
            <CompletionWrapper isCompleted={completedQuest}>
              <Text
                mods={
                  completedQuest ? "bold success nowrap" : "bold grey nowrap"
                }
              >
                {completedQuest
                  ? "Complete"
                  : quest.completion_limit > 1 && progress !== 0
                  ? `${progress}/${quest.completion_limit}`
                  : "Not Complete"}
              </Text>
            </CompletionWrapper>
          )}
          <BalanceChip
            isComplete={completedQuest}
            points={quest.points_rewarded}
            completionLimit={quest.completion_limit}
          />
        </TypeContainer>
      </FlexHeading>
      <ContentContainer>
        {quest.description && (
          <SubtitleText mods="p">{quest.description}</SubtitleText>
        )}
        {!preview && quest.completion_limit > 1 && (
          <div>
            <IndicatorContainer>
              {completedQuest
                ? Array.from(Array(quest.completion_limit), (_) => (
                    <IndicatorNub status={"allCompleted"} />
                  ))
                : Array.from(Array(quest.completion_limit), (_, i) => (
                    <IndicatorNub
                      status={
                        progress !== undefined && i < progress
                          ? "completed"
                          : "incompleted"
                      }
                    />
                  ))}
            </IndicatorContainer>
            <PointsText mods="p">
              {progress === undefined ? 0 : progress}/{quest.completion_limit}{" "}
              scanned
            </PointsText>
          </div>
        )}
      </ContentContainer>
    </StyledDiv>
  );
};

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FlexHeading = styled.div`
  width: 50vw;
  min-width: 100%;
  max-width: 528px;
  display: flex;
  justify-content: space-between;
  padding-bottom: 24px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
    gap: 24px;
  }
`;

const IconOutline = styled.div<{ type: QuestType }>`
  background: ${({ type }) => QuestBackgroundColorMap[type]};
  color: ${({ type }) => QuestPrimaryColorMap[type]};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 32px;
  width: 32px;
  height: 32px;
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CompletionWrapper = styled.div<{ isCompleted?: boolean }>`
  display: flex;
  align-items: center;
  padding-inline: 10px;
  border-radius: 28px;
  height: 32px;
  background-color: ${({ theme, isCompleted }) =>
    isCompleted
      ? theme.globalConstants.color.greenSecondary
      : theme.globalConstants.color.backgroundGrayDark};
`;

const TitleText = styled(Text)`
  color: #374151 !important;
  line-height: normal !important;
`;

const IndicatorContainer = styled.div`
  width: 100%;
  height: 10px;
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const IndicatorNub = styled.div<{
  status: "allCompleted" | "completed" | "incompleted" | "disabled";
}>`
  width: 100%;
  height: 100%;
  border-radius: 32px;
  background: ${({ status, theme }) =>
    status === "allCompleted"
      ? theme.globalConstants.color.backgroundSuccess
      : status === "completed"
      ? theme.globalConstants.color.backgroundPrimary
      : status === "disabled"
      ? theme.globalConstants.color.backgroundGrayDark
      : theme.globalConstants.color.backgroundPrimaryLight};
`;

const SubtitleText = styled(Text)`
  color: #6b7280 !important;
  line-height: normal !important;
  overflow: hidden;
`;

const PointsText = styled(Text)`
  color: #9ca3af !important;
  font-size: 12px !important;
  font-style: normal;
  font-weight: 500 !important;
  line-height: normal;
`;

export default ScannedQuestView;
